import React from 'react';
import { Table, Button, Empty, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import DateWithWeekday from '../../components/DateWithWeekday';
import { SEGMENT_COLOR, PACE_COLOR, PACE_MARK, MUTED_COLOR, WEEKEND_COLOR } from '../../components/metricColors';
import { holidaySampleText } from '../../components/bandSamples';
import { withKeyboardSort } from '../../components/keyboardSort';

/**
 * 前瞻圖的表格版。
 *
 * 不是「順便也給一張表」——它補的是 canvas 本質上補不了的三件事：
 *
 *  1. echarts 畫出來的是一張圖片，螢幕閱讀器讀不到任何一根柱 (WCAG 1.1.1)。
 *  2. 鑽取只綁在 zrender 的 canvas 點擊上，鍵盤完全進不去 (WCAG 2.1.1)。
 *  3. 90 根柱塞進約 800px，每根 8px，遠低於 24x24 的最小目標 (WCAG 2.5.8)；
 *     而且 x 軸標籤靠 hideOverlap 疏開，大半不顯示 —— 想找「某一天」只能滑過去猜。
 *
 * 所以日期那一欄是真的 <button>：Tab 走得到、Enter 按得下，開的是跟點柱一樣的訂房曲線抽屜。
 *
 * 欄寬全部寫死並配 scroll.x：數字欄斷行會讓人讀錯位數，寬度交給自動分配會在
 * 切換粒度時左右跳動 (同 PricingActionsTables 的理由)。
 */

// dat 取 230：內容是「2026/09/14 (一)」再加一個假日/例假日 Tag，與 OrdersDrawer 的 ci 欄同款，
// 那裡實測需要 230 (含排序鈕約 22px)。190 會讓連假那幾列斷成兩行，列高參差就掃不動了
const COL_WIDTH = { dat: 230, occ: 100, rooms: 90, grp: 90, fit: 90, band: 140, status: 110, adr: 110 };
const TABLE_WIDTH = Object.values(COL_WIDTH).reduce((a, b) => a + b, 0);

const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const money = (v) => (v == null ? '—' : `NT$${Math.round(v).toLocaleString()}`);
const num = (k) => (a, b) => (a[k] == null ? -1 : a[k]) - (b[k] == null ? -1 : b[k]);

function columnsFor(onPickDate) {
  return [
    {
      title: '日期', dataIndex: 'dat', key: 'dat', width: COL_WIDTH.dat, fixed: 'left',
      sorter: (a, b) => String(a.dat).localeCompare(String(b.dat)),
      defaultSortOrder: 'ascend',
      render: (d, r) => (onPickDate ? (
        // type="link" 讓它在鍵盤與螢幕閱讀器上都是不折不扣的按鈕，不是帶 onClick 的 span
        <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
          onClick={() => onPickDate(d)} aria-label={`看 ${r.label} 的訂房曲線`}>
          {r.isDaily ? <DateWithWeekday date={d} /> : r.label}
        </Button>
      ) : (r.isDaily ? <DateWithWeekday date={d} /> : r.label)),
    },
    {
      title: '在手率', dataIndex: 'occ', key: 'occ', align: 'right', width: COL_WIDTH.occ,
      sorter: num('occ'), render: pct,
    },
    { title: '在手 (間)', dataIndex: 'rooms', key: 'rooms', align: 'right', width: COL_WIDTH.rooms, sorter: num('rooms') },
    {
      title: '團體 (間)', dataIndex: 'group', key: 'grp', align: 'right', width: COL_WIDTH.grp,
      sorter: num('group'),
      render: (v) => (v == null ? '—' : <span style={{ color: SEGMENT_COLOR.group }}>{v}</span>),
    },
    {
      title: '散客 (間)', dataIndex: 'fit', key: 'fit', align: 'right', width: COL_WIDTH.fit,
      sorter: num('fit'),
      render: (v) => (v == null ? '—' : <span style={{ color: SEGMENT_COLOR.fit }}>{v}</span>),
    },
    {
      // 樣本裡混了國定假日就把它標出來：364 天對齊的是星期，農曆節慶會在樣本之間漂移，
      // 於是一個普通平日的正常帶可能是拿連假撐起來的 (見 bandSamples.js 的說明)
      title: '近年正常帶', key: 'band', align: 'right', width: COL_WIDTH.band,
      render: (_, r) => {
        if (r.bandLo == null) return '—';
        const hol = r.bandHolidaySamples || [];
        const text = `${r.bandLo}–${r.bandHi}%`;
        if (!hol.length) return text;
        const note = holidaySampleText(hol, r.bandYears);
        return (
          <Tooltip title={`${note}，落後幅度會被高估。`}>
            <span style={{ whiteSpace: 'nowrap' }}>
              {text}<WarningOutlined style={{ color: WEEKEND_COLOR, marginLeft: 4 }} aria-label={note} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      // 符號 + 文字，顏色只是第三層 —— 超前與落後的亮度差只有 1.23:1，不能靠顏色分辨
      title: '訂房進度', dataIndex: 'status', key: 'status', width: COL_WIDTH.status,
      sorter: (a, b) => String(a.status || '').localeCompare(String(b.status || '')),
      render: (v) => (v ? (
        <span style={{ color: PACE_COLOR[v], whiteSpace: 'nowrap' }}>
          {PACE_MARK[v] ? `${PACE_MARK[v]} ` : ''}{v}
        </span>
      ) : <span style={{ color: MUTED_COLOR }}>—</span>),
    },
    { title: '在手 ADR', dataIndex: 'adr', key: 'adr', align: 'right', width: COL_WIDTH.adr, sorter: num('adr'), render: money },
  ];
}

export default function ForecastTable({ rows, onPickDate }) {
  if (!rows || !rows.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="這個期間沒有前瞻資料" />;
  }
  return (
    <Table
      rowKey="key" size="small" pagination={false}
      scroll={{ x: TABLE_WIDTH, y: 320 }}
      dataSource={rows} columns={withKeyboardSort(columnsFor(onPickDate))}
    />
  );
}
